<template>
  <div class="container mt-5" style="max-width: 520px;">
    <div class="card shadow-sm">
      <div class="card-body p-4">
        <h5 class="fw-bold mb-4">정기권 신청</h5>

        <!-- 상태 1: 신청 폼 (미신청) -->
        <div v-if="!hasWaiting">
          <div class="mb-3">
            <label class="form-label">주차장 <span class="text-danger">*</span></label>
            <select v-model="form.areaSeq" class="form-select">
              <option value="" disabled>주차장을 선택하세요</option>
              <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
                {{ a.AreaName }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">차량번호</label>
            <input
              :value="carNumber"
              type="text"
              class="form-control"
              readonly
            />
          </div>

          <div class="mb-3">
            <label class="form-label">요금제 <span class="text-danger">*</span></label>
            <select v-model="form.feeSeq" class="form-select">
              <option value="" disabled>요금제를 선택하세요</option>
              <option v-for="f in fees" :key="f.FeeSeq" :value="f.FeeSeq">
                {{ f.FeeName }} - {{ f.Price.toLocaleString() }}원
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">할인</label>
            <select v-model="form.discountSeq" class="form-select">
              <option value="">할인 없음</option>
              <option v-for="d in discounts" :key="d.DiscountSeq" :value="d.DiscountSeq">
                {{ d.DiscountName }} ({{ d.DiscountRate }}%)
              </option>
            </select>
          </div>

          <!-- 증빙서류 파일 업로드 섹션 (할인 선택 시에만 표시) -->
          <div v-if="!form.discountSeq" class="alert alert-info py-2 small mb-3">
            <div>💡 할인을 선택해야 증빙서류를 업로드할 수 있습니다.</div>
          </div>

          <div v-if="form.discountSeq" class="mb-3">
            <label class="form-label">증빙서류 <small class="text-muted">(할인 적용을 위해 필요합니다)</small></label>
            <div
              class="drop-area mb-3"
              @drop.prevent="onDrop"
              @dragover.prevent
              @click="triggerFileInput"
            >
              <input ref="fileInput" type="file" multiple @change="onInputFiles" class="d-none" />
              <div class="text-center p-3">
                <p class="mb-1 small">파일을 여기에 끌어다 놓거나 클릭해서 선택하세요</p>
                <small class="text-muted">최대 5개, 각 10MB 이하</small>
              </div>
            </div>

            <div v-if="files.length" class="mb-3">
              <div class="file-list">
                <div v-for="(f, i) in files" :key="f.id" class="file-item d-flex align-items-center mb-2">
                  <div class="thumb me-2" v-if="f.preview">
                    <img :src="f.preview" alt="preview" />
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between">
                      <div>
                        <strong class="small">{{ f.file.name }}</strong>
                        <div class="text-muted small">{{ formatBytes(f.file.size) }}</div>
                      </div>
                      <div>
                        <button class="btn btn-sm btn-outline-danger" @click="removeFile(i)" :disabled="uploading">제거</button>
                      </div>
                    </div>

                    <div class="progress mt-2" style="height:6px" v-if="f.progress > 0">
                      <div class="progress-bar" :style="{ width: f.progress + '%' }"></div>
                    </div>

                    <div class="small text-muted mt-1">
                      <span v-if="f.status === 'pending'">대기중</span>
                      <span v-if="f.status === 'uploading'">전송중...</span>
                      <span v-if="f.status === 'done'" class="text-success">완료</span>
                      <span v-if="f.status === 'error'" class="text-danger">오류</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 기존 정기권 사용자 여부 -->
          <div class="mb-3 form-check">
            <input 
              v-model="form.isExistingUser" 
              type="checkbox" 
              class="form-check-input" 
              id="isExistingUserCheck"
            />
            <label class="form-check-label" for="isExistingUserCheck">
              기존 정기권 사용자입니다.(현재 정기권 사용자일 경우 체크해주세요)
            </label>
          </div>

          <div v-if="selectedFee || form.discountSeq" class="alert alert-info py-2 small mb-3">
            <div>요금: <strong>{{ selectedFeePrice.toLocaleString() }}원</strong></div>
            <div v-if="selectedDiscount">할인율: <strong>{{ selectedDiscount.DiscountRate }}%</strong></div>
            <div class="mt-2 border-top pt-2">최종 가격: <strong class="text-success">{{ finalPrice.toLocaleString() }}원</strong></div>
          </div>

          <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

          <button class="btn btn-dark w-100" :disabled="loading || !form.areaSeq || !form.feeSeq" @click="submit">
            {{ loading ? '신청 중...' : '신청' }}
          </button>
        </div>

        <!-- 상태 2: 신청 완료 (방금 신청함) -->
        <div v-else-if="isNewlySubmitted" class="text-center">
          <div class="alert alert-success py-4 mb-4">
            <div class="mb-3" style="font-size: 2.5rem;">✓</div>
            <div class="fs-5 fw-bold mb-3">정기권 신청 완료 되었습니다</div>
            <div class="small text-secondary">
              <div>주차장: {{ existingWaiting.areaName }}</div>
              <div>요금제: {{ existingWaiting.feeName }}</div>
              <div v-if="existingWaiting.discountName">할인: {{ existingWaiting.discountName }}</div>
            </div>
          </div>
          <div v-if="existingWaiting.status === 'LOTTERY_COMPLETED'" class="alert alert-warning mb-0">
            <div class="mb-2">🔒 추첨이 완료되었습니다</div>
            <div class="small">취소가 불가능합니다. 자세한 사항은 관리자에게 문의 부탁드립니다.</div>
          </div>
          <button v-else class="btn btn-outline-danger w-100" :disabled="loading" @click="cancel">
            {{ loading ? '취소 중...' : '신청 취소' }}
          </button>
        </div>

        <!-- 상태 3: 이미 신청됨 (페이지 재진입) -->
        <div v-else class="text-center">
          <div class="alert alert-warning py-4 mb-4">
            <div class="mb-3" style="font-size: 2.5rem;">!</div>
            <div class="fs-5 fw-bold mb-3">이미 신청되었습니다</div>
            <div class="small text-secondary">
              <div>주차장: {{ existingWaiting.areaName }}</div>
              <div>요금제: {{ existingWaiting.feeName }}</div>
              <div v-if="existingWaiting.discountName">할인: {{ existingWaiting.discountName }}</div>
            </div>
          </div>
          <div v-if="existingWaiting.status === 'LOTTERY_COMPLETED'" class="alert alert-warning mb-0">
            <div class="mb-2">🔒 추첨이 완료되었습니다</div>
            <div class="small">취소가 불가능합니다. 자세한 사항은 관리자에게 문의 부탁드립니다.</div>
          </div>
          <button v-else class="btn btn-outline-danger w-100" :disabled="loading" @click="cancel">
            {{ loading ? '취소 중...' : '신청 취소' }}
          </button>
        </div>

        <div v-if="error" class="alert alert-danger py-2 mt-3">{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ParkingPassWaitingRegView',
  data() {
    return {
      areas: [],
      fees: [],
      discounts: [],
      carNumber: '',
      hasWaiting: false,
      isNewlySubmitted: false,
      existingWaiting: {},
      form: { areaSeq: '', feeSeq: '', discountSeq: '', isExistingUser: false },
      loading: false,
      uploading: false,
      error: null,
      success: false,
      files: [],
      fileIdSeq: 1
    }
  },
  computed: {
    selectedArea() {
      return this.areas.find(a => a.AreaSeq === this.form.areaSeq) || null
    },
    selectedFee() {
      return this.fees.find(f => f.FeeSeq === this.form.feeSeq) || null
    },
    selectedDiscount() {
      return this.discounts.find(d => d.DiscountSeq === this.form.discountSeq) || null
    },
    selectedFeePrice() {
      return this.selectedFee?.Price || 0
    },
    finalPrice() {
      if (!this.selectedFee) return 0
      const price = this.selectedFee.Price
      if (!this.selectedDiscount) return price
      const discountRate = this.selectedDiscount.DiscountRate
      return Math.floor(price * (100 - discountRate) / 100)
    }
  },
  watch: {
    'form.areaSeq': function() {
      this.fees = []
      this.form.feeSeq = ''
      if (this.form.areaSeq) {
        this.fetchFees()
      }
    },
    'form.discountSeq': function(newVal) {
      // 할인이 해제되면 파일 목록 초기화
      if (!newVal) {
        for (const f of this.files) if (f.preview) URL.revokeObjectURL(f.preview)
        this.files = []
      }
    }
  },
  mounted() {
    this.loadUserCarNumber()
    this.initializeData()
  },
  methods: {
    async initializeData() {
      await Promise.all([
        this.fetchAreas(),
        this.fetchDiscounts()
      ])
      await this.checkExistingWaiting()
    },
    loadUserCarNumber() {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}')
      this.carNumber = user.CarNumber || ''
    },
    async checkExistingWaiting() {
      try {
        const user = JSON.parse(sessionStorage.getItem('user') || '{}')
        if (!user.UserSeq) {
          this.hasWaiting = false
          this.isNewlySubmitted = false
          return
        }
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting/check?userSeq=${user.UserSeq}`)
        if (res.data.exists) {
          // 기존 신청 정보 저장 (백엔드에서 이미 JOIN으로 받음)
          const waiting = res.data.data
          this.existingWaiting = {
            waitingSeq: waiting.WaitingSeq,
            areaName: waiting.AreaName || '알 수 없음',
            feeName: waiting.FeeName || '알 수 없음',
            discountName: waiting.DiscountName || null,
            status: waiting.Status || null
          }
          this.hasWaiting = true
          this.isNewlySubmitted = false
        } else {
          this.hasWaiting = false
          this.isNewlySubmitted = false
        }
      } catch (e) {
        console.error('checkExistingWaiting error:', e)
        this.hasWaiting = false
        this.isNewlySubmitted = false
      }
    },
    async fetchAreas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-areas/available`)
        this.areas = res.data
      } catch {
        this.error = '주차장 목록을 불러오지 못했습니다.'
      }
    },
    async fetchFees() {
      try {
        const area = this.selectedArea
        if (!area) return
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-fee?areaCode=${encodeURIComponent(area.AreaCode)}`)
        this.fees = res.data
      } catch (e) {
        this.error = '요금제를 불러오지 못했습니다.'
      }
    },
    async fetchDiscounts() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/discount`)
        this.discounts = res.data
      } catch (e) {
        this.error = '할인 목록을 불러오지 못했습니다.'
      }
    },
    async submit() {
      this.error = null
      this.success = false
      const { areaSeq, feeSeq } = this.form
      if (!areaSeq || !feeSeq) {
        this.error = '주차장과 요금제를 선택하세요.'
        return
      }
      const user = JSON.parse(sessionStorage.getItem('user') || '{}')
      if (!user.UserSeq) {
        this.$router.push('/login')
        return
      }
      this.loading = true
      try {
        const selectedArea = this.areas.find(a => a.AreaSeq === parseInt(areaSeq))
        const selectedFee = this.fees.find(f => f.FeeSeq === parseInt(feeSeq))
        const selectedDiscount = this.form.discountSeq ? this.discounts.find(d => d.DiscountSeq === parseInt(this.form.discountSeq)) : null
        
        const res = await axios.post(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting`, {
          userSeq: user.UserSeq,
          areaSeq: parseInt(areaSeq),
          feeSeq: parseInt(feeSeq),
          discountSeq: this.form.discountSeq ? parseInt(this.form.discountSeq) : null,
          isExistingUser: this.form.isExistingUser ? 1 : 0
        })
        
        const waitingSeq = res.data.WaitingSeq
        
        // 파일이 있으면 업로드
        if (this.files.length > 0) {
          await this.uploadFiles(waitingSeq)
        }
        
        // 신청 완료 상태 설정
        this.hasWaiting = true
        this.isNewlySubmitted = true
        this.existingWaiting = {
          waitingSeq: waitingSeq,
          areaName: selectedArea?.AreaName || '알 수 없음',
          feeName: selectedFee?.FeeName || '알 수 없음',
          discountName: selectedDiscount?.DiscountName || null,
          status: 'WAITING'
        }
      } catch (e) {
        this.error = e.response?.data?.message || '신청에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    triggerFileInput() {
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onInputFiles(e) {
      const list = e.target.files
      this.handleFiles(list)
      e.target.value = ''
    },
    onDrop(e) {
      const list = e.dataTransfer.files
      this.handleFiles(list)
    },
    handleFiles(fileList) {
      if (this.files.length >= 5) {
        this.error = '최대 5개까지만 선택할 수 있습니다.'
        return
      }
      for (const f of Array.from(fileList)) {
        // 중복 체크
        if (this.files.some(x => x.file.name === f.name && x.file.size === f.size)) continue
        
        // 파일 크기 체크 (10MB)
        if (f.size > 10 * 1024 * 1024) {
          this.error = `${f.name}은 10MB 이상으로 업로드할 수 없습니다.`
          continue
        }
        
        // 파일 개수 체크
        if (this.files.length >= 5) {
          this.error = '최대 5개까지만 선택할 수 있습니다.'
          break
        }
        
        const item = {
          id: this.fileIdSeq++,
          file: f,
          preview: null,
          progress: 0,
          status: 'pending'
        }
        if (f.type.startsWith('image/')) {
          item.preview = URL.createObjectURL(f)
        }
        this.files.push(item)
      }
    },
    removeFile(idx) {
      const it = this.files.splice(idx, 1)[0]
      if (it && it.preview) URL.revokeObjectURL(it.preview)
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let i = 0
      let v = bytes
      while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
      return v.toFixed((i === 0) ? 0 : 2) + ' ' + units[i]
    },
    async uploadFiles(waitingSeq) {
      if (!this.files.length) return
      this.uploading = true
      try {
        for (const fObj of this.files) {
          fObj.status = 'uploading'
          fObj.progress = 0
          const form = new FormData()
          form.append('files', fObj.file)
        
          await axios.post(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting-upload?waitingSeq=${waitingSeq}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (evt) => {
              if (evt.total) {
                fObj.progress = Math.round((evt.loaded / evt.total) * 100)
              }
            }
          })
          fObj.status = 'done'
          fObj.progress = 100
        }
      } catch (err) {
        console.error('file upload error', err)
        const cur = this.files.find(x => x.status === 'uploading')
        if (cur) cur.status = 'error'
        throw err
      } finally {
        this.uploading = false
      }
    },
    async cancel() {
      if (!confirm('신청을 취소하시겠습니까?')) {
        return
      }
      if (!this.existingWaiting.waitingSeq) return
      this.loading = true
      this.error = null
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting?waitingSeq=${this.existingWaiting.waitingSeq}`)
        this.hasWaiting = false
        this.isNewlySubmitted = false
        this.existingWaiting = {}
        this.form = { areaSeq: '', feeSeq: '', discountSeq: '', isExistingUser: false }
        this.fees = []
        // 파일 목록 초기화
        for (const f of this.files) if (f.preview) URL.revokeObjectURL(f.preview)
        this.files = []
      } catch (e) {
        this.error = e.response?.data?.message || '청구서가 연결되어 있어 취소할 수 없습니다. 관리자에게 청구서 삭제를 먼저 요청하세요.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.drop-area {
  border: 2px dashed #cfd8dc;
  border-radius: 8px;
  cursor: pointer;
  background: #fafafa;
  transition: background-color 0.2s;
}
.drop-area:hover {
  background: #f4f7f9;
}
.drop-area input {
  display: none;
}
.file-item {
  padding: 8px;
  border: 1px solid #eef2f4;
  border-radius: 6px;
}
.thumb {
  width: 64px;
  height: 48px;
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}
.thumb img {
  max-width: 100%;
  max-height: 100%;
  display: block;
}
.progress {
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}
.progress-bar {
  height: 100%;
  background: #4caf50;
  color: #fff;
  font-size: 11px;
  text-align: center;
  line-height: 6px;
}
</style>
